// src/views/admin/caisse/ADCaisseBilan.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../../services/adminService";
import { Loader } from "../../../components/Loader";
import { useToastHook } from "../../../utils/useToast";
import { usePermissions } from "../../../context/PermissionsContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ============================================================================
 * BILAN GLOBAL — Scolarité + Trésorerie
 * Point d'entrée exporté par défaut, importé par ADCaisse.jsx
 *
 * Filtres :
 *  - Année scolaire : scope le bloc "Scolarité" (dû/payé/reste par classe
 *    et par type de frais) — une situation d'engagement est cumulative,
 *    jamais limitée à une période.
 *  - Date début / Date fin : scope le bloc "Trésorerie" (versements,
 *    encaissements, décaissements). Laisser vide = depuis toujours.
 *
 * PERMISSIONS (module "caisse") :
 * - caisse.voir_bilan     : accès à cet onglet — déjà filtré en amont dans
 *                           ADCaisse.jsx, l'onglet n'est même pas monté si
 *                           l'utilisateur ne l'a pas.
 * - caisse.exporter_bilan : boutons "Télécharger PDF" / "Imprimer"

 *

 * ========================================================================== */
export default function BilanCaisse({ slug }) {
  const { canSee } = usePermissions();
  const peutExporter = canSee("caisse.exporter_bilan");

  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");

  const [bilan, setBilan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingBilan, setLoadingBilan] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const toast = useToastHook();
  const { getAnneesScolaires, getBilanCaisse } = useAdminService();

  useEffect(() => { if (slug) init(); }, [slug]);
  useEffect(() => { if (slug && anneeFilt) loadBilan(); }, [slug, anneeFilt, dateDebut, dateFin]);

  const init = async () => {
    setLoading(true);
    try {
      const anneesData = await getAnneesScolaires(slug, { show_archived: "false" });
      setAnnees(anneesData);
      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les années scolaires");
    } finally {
      setLoading(false);
    }
  };

  const loadBilan = async () => {
    setLoadingBilan(true);
    try {
      const params = { annee_scolaire: anneeFilt };
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      setBilan(await getBilanCaisse(slug, params));
    } catch (error) {
      toast.error("Impossible de charger le bilan");
    } finally {
      setLoadingBilan(false);
    }
  };

  const reinitialiserPeriode = () => { setDateDebut(""); setDateFin(""); };

  const anneeSelectionnee = annees.find(a => String(a.id) === String(anneeFilt));
  const periodeLabel = (dateDebut || dateFin)
    ? `Trésorerie du ${dateDebut || "…"} au ${dateFin || "…"}`
    : "Trésorerie depuis toujours";

  // ─── PDF (jsPDF + autoTable — même pattern que genererPDFListe) ─────────
  const genererPDFBilan = () => {
    if (!bilan) return;
    setGeneratingPDF(true);
    try {
      const doc = new jsPDF("portrait", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const dateGen = new Date().toLocaleDateString("fr-FR");
      const anneeNom = anneeSelectionnee?.nom || "";

      // Marge haute réservée pour les pages ajoutées automatiquement par
      // autoTable (évite qu'un tableau qui déborde reprenne collé en haut
      // de la page suivante, sans respirer).
      const TOP_MARGIN = 18;
      const BOTTOM_MARGIN = 20;

      // IMPORTANT : Number(x).toLocaleString() insère, selon l'environnement,
      // une espace fine insécable (U+202F) comme séparateur de milliers.
      // Cette espace n'existe pas dans l'encodage des polices standard de
      // jsPDF (Helvetica/WinAnsi) : jsPDF la remplace alors par un mauvais
      // glyphe (le "/" qu'on voit dans le PDF, ex. "2 /100 /000"). On formate
      // donc les montants nous-mêmes avec une espace normale, sans dépendre
      // d'une locale.
      const formatMontant = (n) => {
        const val = Math.round(Number(n) || 0);
        const neg = val < 0;
        const digits = Math.abs(val).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
        return neg ? `-${digits}` : digits;
      };

      doc.setFontSize(16);
      doc.setTextColor(0, 64, 128);
      doc.setFont(undefined, "bold");
      doc.text("BILAN DE LA CAISSE", pageWidth / 2, 18, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.setFont(undefined, "normal");
      doc.text(`Année scolaire : ${anneeNom || "—"}`, pageWidth / 2, 27, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(80, 80, 80);
      doc.text(periodeLabel, pageWidth / 2, 34, { align: "center" });

      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      doc.text(`Généré le ${dateGen}`, pageWidth / 2, 40, { align: "center" });

      doc.setDrawColor(0, 64, 128);
      doc.setLineWidth(0.5);
      doc.line(14, 43, pageWidth - 14, 43);

      let y = 50;

      const checkPageBreak = (neededHeight) => {
        if (y + neededHeight > pageHeight - 16) {
          doc.addPage();
          y = TOP_MARGIN;
        }
      };

      const sectionTitle = (titre) => {
        checkPageBreak(12);
        doc.setFontSize(12);
        doc.setTextColor(0, 64, 128);
        doc.setFont(undefined, "bold");
        doc.text(titre, 14, y);
        y += 6;
        doc.setFont(undefined, "normal");
        doc.setTextColor(0, 0, 0);
      };

      const kpiLine = (label, valeur) => {
        checkPageBreak(6);
        doc.setFontSize(10);
        doc.text(`${label} : ${valeur}`, 14, y);
        y += 5.5;
      };

      // Avant de démarrer un tableau, on s'assure qu'il reste assez de
      // place pour l'en-tête + au moins 2 lignes de données — sinon
      // l'en-tête se retrouve orphelin en bas de page et le tableau
      // "saute" une page en laissant un trou. On calcule la hauteur
      // réelle attendue à partir du nombre de lignes du tableau.
      const ROW_H = 8.5; // hauteur approx. d'une ligne (fontSize 9 + cellPadding 3, marge incluse)
      const prepareTable = (rowCount) => {
        const minNeeded = ROW_H * Math.min(rowCount + 1, 3); // en-tête + 2 lignes mini
        checkPageBreak(minNeeded);
      };

      const tableStyle = {
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 3, textColor: [0, 0, 0], valign: "middle" },
        headStyles: { fillColor: [0, 64, 128], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
        alternateRowStyles: { fillColor: [240, 246, 255] },
        margin: { left: 14, right: 14, top: TOP_MARGIN, bottom: BOTTOM_MARGIN },
      };

      // ── Scolarité ──
      sectionTitle("Situation des frais de scolarité");
      kpiLine("Total dû (net remises)", `${formatMontant(bilan.scolarite.total_du_net)} FCFA`);
      kpiLine("Total encaissé", `${formatMontant(bilan.scolarite.total_paye)} FCFA`);
      kpiLine("Reste à payer", `${formatMontant(bilan.scolarite.total_reste)} FCFA`);
      kpiLine("Taux de recouvrement", `${bilan.scolarite.taux_recouvrement}%`);
      kpiLine(
        "Élèves",
        `${bilan.scolarite.nb_eleves}  (à jour : ${bilan.scolarite.nb_a_jour} · partiel : ${bilan.scolarite.nb_partiel} · aucun versement : ${bilan.scolarite.nb_aucun_versement})`
      );
      y += 2;

      prepareTable(bilan.par_classe.length);
      autoTable(doc, {
        startY: y,
        head: [["Classe", "Effectif", "Dû", "Payé", "Reste"]],
        body: bilan.par_classe.map(c => [
          c.classe, c.effectif,
          formatMontant(c.total_du),
          formatMontant(c.total_paye),
          formatMontant(c.total_reste),
        ]),
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "center" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right", fontStyle: "bold" },
        },
        ...tableStyle,
      });
      y = doc.lastAutoTable.finalY + 8;

      prepareTable(bilan.par_type_frais.length);
      autoTable(doc, {
        startY: y,
        head: [["Type de frais", "Dû", "Payé", "Reste"]],
        body: bilan.par_type_frais.map(t => [
          t.type_frais,
          formatMontant(t.total_du),
          formatMontant(t.total_paye),
          formatMontant(t.total_reste),
        ]),
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right", fontStyle: "bold" },
        },
        ...tableStyle,
      });
      y = doc.lastAutoTable.finalY + 10;

      // ── Trésorerie ──
      sectionTitle("Trésorerie");
      kpiLine("Total encaissé (période)", `${formatMontant(bilan.tresorerie.total_encaissements)} FCFA`);
      kpiLine("Dépenses réelles (période)", `${formatMontant(bilan.tresorerie.total_decaissements_reels)} FCFA`);
      if (Number(bilan.tresorerie.total_annulations) > 0) {
        kpiLine(
          "Annulations de versements",
          `${formatMontant(bilan.tresorerie.total_annulations)} FCFA (${bilan.tresorerie.nb_annulations})`
        );
      }
      kpiLine("Solde net (période)", `${formatMontant(bilan.tresorerie.solde_net_periode)} FCFA`);
      kpiLine("Trésorerie totale (toutes caisses)", `${formatMontant(bilan.tresorerie.tresorerie_totale)} FCFA`);
      y += 2;

      prepareTable(bilan.caisses.length);
      autoTable(doc, {
        startY: y,
        head: [["Caisse", "Solde actuel", "Encaissé", "Décaissé", "Transf. +", "Transf. -"]],
        body: bilan.caisses.map(c => [
          c.nom,
          `${formatMontant(c.solde_actuel)} FCFA`,
          formatMontant(c.encaissements_periode),
          formatMontant(c.decaissements_periode),
          formatMontant(c.transferts_entree_periode),
          formatMontant(c.transferts_sortie_periode),
        ]),
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right", fontStyle: "bold" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
        },
        ...tableStyle,
        styles: { ...tableStyle.styles, fontSize: 8.5 },
      });
      y = doc.lastAutoTable.finalY + 8;

      prepareTable(bilan.par_mode_paiement.length);
      autoTable(doc, {
        startY: y,
        head: [["Mode de paiement", "Montant", "Nombre"]],
        body: bilan.par_mode_paiement.map(m => [
          m.mode_paiement || "—", `${formatMontant(m.total)} FCFA`, m.nombre,
        ]),
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right" },
          2: { halign: "center" },
        },
        ...tableStyle,
      });
      y = doc.lastAutoTable.finalY + 8;

      prepareTable(bilan.par_rubrique_depense.length);
      autoTable(doc, {
        startY: y,
        head: [["Rubrique de dépense", "Montant", "Nombre"]],
        body: bilan.par_rubrique_depense.map(r => [
          r.rubrique || "—", `${formatMontant(r.total)} FCFA`, r.nombre,
        ]),
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right" },
          2: { halign: "center" },
        },
        ...tableStyle,
      });
      y = doc.lastAutoTable.finalY + 8;

      if (bilan.annulations_versements.length > 0) {
        prepareTable(bilan.annulations_versements.length);
        autoTable(doc, {
          startY: y,
          head: [["Reçu", "Date", "Montant", "Caisse", "Motif"]],
          body: bilan.annulations_versements.map(a => [
            a.numero_recu || "—", a.date, `${formatMontant(a.montant)} FCFA`, a.caisse, a.motif || "—",
          ]),
          columnStyles: {
            0: { halign: "left" },
            1: { halign: "center" },
            2: { halign: "right" },
            3: { halign: "left" },
            4: { halign: "left" },
          },
          ...tableStyle,
          styles: { ...tableStyle.styles, fontSize: 8 },
        });
      }

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: "center" });
      }

      const suffixe = anneeNom ? anneeNom.replace(/\s+/g, "-") : "annee";
      doc.save(`bilan-caisse-${suffixe}-${dateGen.replace(/\//g, "-")}.pdf`);
      toast.success("PDF généré avec succès");
    } catch (error) {
      toast.error("Impossible de générer le PDF");
    } finally {
      setGeneratingPDF(false);
    }
  };

  // ─── Impression (fenêtre HTML dédiée — même pattern que imprimerListe) ──
  const imprimerBilan = () => {
    if (!bilan) return;
    const dateGen = new Date().toLocaleDateString("fr-FR");
    const anneeNom = anneeSelectionnee?.nom || "";

    const td = (v, align = "left") =>
      `<td style="padding:6px 8px;border:1px solid #ccc;text-align:${align};">${v}</td>`;

    const ligneClasses = bilan.par_classe.map((c, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(c.classe)}${td(c.effectif, "center")}
        ${td(Number(c.total_du).toLocaleString(), "right")}
        ${td(Number(c.total_paye).toLocaleString(), "right")}
        ${td(Number(c.total_reste).toLocaleString(), "right")}
      </tr>`).join("");

    const ligneTypesFrais = bilan.par_type_frais.map((t, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(t.type_frais)}
        ${td(Number(t.total_du).toLocaleString(), "right")}
        ${td(Number(t.total_paye).toLocaleString(), "right")}
        ${td(Number(t.total_reste).toLocaleString(), "right")}
      </tr>`).join("");

    const ligneCaisses = bilan.caisses.map((c, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(c.nom)}
        ${td(`${Number(c.solde_actuel).toLocaleString()} FCFA`, "right")}
        ${td(Number(c.encaissements_periode).toLocaleString(), "right")}
        ${td(Number(c.decaissements_periode).toLocaleString(), "right")}
        ${td(Number(c.transferts_entree_periode).toLocaleString(), "right")}
        ${td(Number(c.transferts_sortie_periode).toLocaleString(), "right")}
      </tr>`).join("");

    const ligneModes = bilan.par_mode_paiement.map((m, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(m.mode_paiement || "—")}
        ${td(`${Number(m.total).toLocaleString()} FCFA`, "right")}
        ${td(m.nombre, "center")}
      </tr>`).join("");

    const ligneRubriques = bilan.par_rubrique_depense.map((r, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(r.rubrique || "—")}
        ${td(`${Number(r.total).toLocaleString()} FCFA`, "right")}
        ${td(r.nombre, "center")}
      </tr>`).join("");

    const ligneAnnulations = bilan.annulations_versements.map((a, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0f6ff"}">
        ${td(a.numero_recu || "—")}
        ${td(a.date)}
        ${td(`${Number(a.montant).toLocaleString()} FCFA`, "right")}
        ${td(a.caisse)}
        ${td(a.motif || "—")}
      </tr>`).join("");

    const html = `
      <!DOCTYPE html>
      <html>
        <head><meta charset="UTF-8"><title>Bilan de la caisse${anneeNom ? " - " + anneeNom : ""}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Times New Roman', serif; padding: 20px; background: white; color: #000; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            .header { text-align: center; margin-bottom: 18px; border-bottom: 2px solid #004080; padding-bottom: 12px; }
            .header h1 { font-size: 20pt; color: #004080; text-transform: uppercase; margin-bottom: 6px; }
            .header .sous-titre { font-size: 13pt; font-weight: bold; color: #222; margin-bottom: 4px; }
            .header .meta { font-size: 10pt; color: #555; }
            h3.section { font-size: 13pt; color: #004080; margin: 18px 0 8px; border-left: 4px solid #004080; padding-left: 8px; }
            .kpis { font-size: 10.5pt; margin-bottom: 8px; }
            .kpis div { padding: 2px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; margin-bottom: 4px; font-size: 10pt; }
            thead tr { background: #004080 !important; color: white; }
            thead th { padding: 7px 8px; border: 1px solid #003366; text-align: center; font-weight: bold; }
            .note { font-size: 9pt; color: #666; margin: 4px 0 10px; }
            tfoot td { font-size: 9pt; color: #666; text-align: center; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Bilan de la caisse</h1>
            <div class="sous-titre">Année scolaire : ${anneeNom || "—"}</div>
            <div class="meta">${periodeLabel} &nbsp;·&nbsp; Généré le ${dateGen}</div>
          </div>

          <h3 class="section">Situation des frais de scolarité</h3>
          <div class="kpis">
            <div>Total dû (net remises) : <strong>${Number(bilan.scolarite.total_du_net).toLocaleString()} FCFA</strong></div>
            <div>Total encaissé : <strong>${Number(bilan.scolarite.total_paye).toLocaleString()} FCFA</strong></div>
            <div>Reste à payer : <strong>${Number(bilan.scolarite.total_reste).toLocaleString()} FCFA</strong></div>
            <div>Taux de recouvrement : <strong>${bilan.scolarite.taux_recouvrement}%</strong></div>
            <div>Élèves : <strong>${bilan.scolarite.nb_eleves}</strong> (à jour : ${bilan.scolarite.nb_a_jour} · partiel : ${bilan.scolarite.nb_partiel} · aucun versement : ${bilan.scolarite.nb_aucun_versement})</div>
          </div>
          <table>
            <thead><tr><th>Classe</th><th>Effectif</th><th>Dû</th><th>Payé</th><th>Reste</th></tr></thead>
            <tbody>${ligneClasses}</tbody>
          </table>
          <table>
            <thead><tr><th>Type de frais</th><th>Dû</th><th>Payé</th><th>Reste</th></tr></thead>
            <tbody>${ligneTypesFrais}</tbody>
          </table>

          <h3 class="section">Trésorerie</h3>
          <div class="kpis">
            <div>Total encaissé (période) : <strong>${Number(bilan.tresorerie.total_encaissements).toLocaleString()} FCFA</strong></div>
            <div>Dépenses réelles (période) : <strong>${Number(bilan.tresorerie.total_decaissements_reels).toLocaleString()} FCFA</strong></div>
            ${Number(bilan.tresorerie.total_annulations) > 0
              ? `<div>Annulations de versements : <strong>${Number(bilan.tresorerie.total_annulations).toLocaleString()} FCFA</strong> (${bilan.tresorerie.nb_annulations})</div>`
              : ""}
            <div>Solde net (période) : <strong>${Number(bilan.tresorerie.solde_net_periode).toLocaleString()} FCFA</strong></div>
            <div>Trésorerie totale (toutes caisses) : <strong>${Number(bilan.tresorerie.tresorerie_totale).toLocaleString()} FCFA</strong></div>
          </div>
          <table>
            <thead><tr><th>Caisse</th><th>Solde actuel</th><th>Encaissé</th><th>Décaissé</th><th>Transf. reçus</th><th>Transf. envoyés</th></tr></thead>
            <tbody>${ligneCaisses}</tbody>
          </table>
          <div class="note">Les transferts entre caisses ne sont ni une recette ni une dépense pour l'établissement — l'argent change simplement de caisse.</div>

          <table>
            <thead><tr><th>Mode de paiement</th><th>Montant</th><th>Nombre</th></tr></thead>
            <tbody>${ligneModes}</tbody>
          </table>
          <table>
            <thead><tr><th>Rubrique de dépense</th><th>Montant</th><th>Nombre</th></tr></thead>
            <tbody>${ligneRubriques}</tbody>
          </table>
          ${bilan.annulations_versements.length > 0 ? `
            <table>
              <thead><tr><th>Reçu</th><th>Date</th><th>Montant</th><th>Caisse</th><th>Motif</th></tr></thead>
              <tbody>${ligneAnnulations}</tbody>
            </table>
          ` : ""}

          <script>
            window.onload = function() { setTimeout(function() { window.print(); }, 300); };
            window.onafterprint = function() { window.close(); };
          <\/script>
        </body>
      </html>
    `;

    const printWindow = window.open("", "_blank", "width=900,height=700,scrollbars=yes");
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <div className="fg" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <label style={{ margin: 0 }}>Du</label>
          <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
        </div>
        <div className="fg" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          <label style={{ margin: 0 }}>Au</label>
          <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
        </div>
        {(dateDebut || dateFin) && (
          <button className="bsec" onClick={reinitialiserPeriode}><i className="ti ti-x" />Période</button>
        )}
        <button className="bsec" onClick={loadBilan}><i className="ti ti-refresh" />Actualiser</button>
        {/* caisse.exporter_bilan */}
        {peutExporter && (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              className="bsec"
              onClick={genererPDFBilan}
              disabled={!bilan || generatingPDF}
              style={{ color: "#dc3545", borderColor: "#dc3545" }}
            >
              <i className="ti ti-file-pdf" /> {generatingPDF ? "Génération..." : "Télécharger PDF"}
            </button>
            <button
              className="bsec"
              onClick={imprimerBilan}
              disabled={!bilan}
              style={{ color: "#6f42c1", borderColor: "#6f42c1" }}
            >
              <i className="ti ti-printer" />Imprimer
            </button>
          </div>
        )}
      </div>

      {loadingBilan || !bilan ? <Loader /> : (
        <>
          {/* ── Scolarité ────────────────────────────────────── */}
          <SectionTitle icon="ti-report-money" titre="Situation des frais de scolarité" />
          <div className="sg" style={{ gridTemplateColumns: "repeat(4, minmax(0,1fr))", marginBottom: 14 }}>
            <KpiCard label="Total dû (net remises)" valeur={bilan.scolarite.total_du_net} accent="accent" />
            <KpiCard label="Total encaissé" valeur={bilan.scolarite.total_paye} accent="accent-gn" />
            <KpiCard label="Reste à payer" valeur={bilan.scolarite.total_reste} accent="accent-rd" />
            <KpiCard
              label="Taux de recouvrement"
              valeur={`${bilan.scolarite.taux_recouvrement}%`}
              brut
              accent="accent-bl"
            />
          </div>
          <div className="sg" style={{ gridTemplateColumns: "repeat(3, minmax(0,1fr))", marginBottom: 14 }}>
            <MiniStat label="Élèves à jour" valeur={bilan.scolarite.nb_a_jour} total={bilan.scolarite.nb_eleves} pill="ok" />
            <MiniStat label="Versement partiel" valeur={bilan.scolarite.nb_partiel} total={bilan.scolarite.nb_eleves} pill="warn" />
            <MiniStat label="Aucun versement" valeur={bilan.scolarite.nb_aucun_versement} total={bilan.scolarite.nb_eleves} pill="err" />
          </div>
          {bilan.scolarite.total_remises > 0 && (
            <div style={{ fontSize: 12, color: "var(--h)", marginBottom: 14, marginTop: -6 }}>
              Dont {Number(bilan.scolarite.total_remises).toLocaleString()} FCFA de remises accordées
              (dû brut : {Number(bilan.scolarite.total_du).toLocaleString()} FCFA)
            </div>
          )}

          {/* ── Détail par classe ───────────────────────────── */}
          <div className="card" style={{ padding: 0, overflowX: "auto", marginBottom: 14 }}>
            <div className="ch" style={{ padding: "12px 14px 0" }}><div className="ct">Détail par classe</div></div>
            <table className="tbl" style={{ minWidth: 620 }}>
              <thead>
                <tr>
                  <th>Classe</th>
                  <th style={{ whiteSpace: "nowrap" }}>Effectif</th>
                  <th style={{ whiteSpace: "nowrap" }}>Dû</th>
                  <th style={{ whiteSpace: "nowrap" }}>Payé</th>
                  <th style={{ whiteSpace: "nowrap" }}>Reste</th>
                  <th style={{ whiteSpace: "nowrap" }}>Recouvrement</th>
                </tr>
              </thead>
              <tbody>
                {bilan.par_classe.map(c => (
                  <tr key={c.classe_id}>
                    <td style={{ fontWeight: 500 }}>{c.classe}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{c.effectif}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{Number(c.total_du).toLocaleString()}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{Number(c.total_paye).toLocaleString()}</td>
                    <td style={{ whiteSpace: "nowrap", color: Number(c.total_reste) > 0 ? "var(--rd)" : "var(--gn)", fontWeight: 600 }}>
                      {Number(c.total_reste).toLocaleString()}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <TauxBar du={c.total_du} paye={c.total_paye} />
                    </td>
                  </tr>
                ))}
                {bilan.par_classe.length === 0 && (
                  <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune classe pour cette année</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Détail par type de frais ────────────────────── */}
          <div className="card" style={{ padding: 0, overflowX: "auto", marginBottom: 20 }}>
            <div className="ch" style={{ padding: "12px 14px 0" }}><div className="ct">Détail par type de frais</div></div>
            <table className="tbl" style={{ minWidth: 500 }}>
              <thead>
                <tr>
                  <th>Type de frais</th>
                  <th style={{ whiteSpace: "nowrap" }}>Dû</th>
                  <th style={{ whiteSpace: "nowrap" }}>Payé</th>
                  <th style={{ whiteSpace: "nowrap" }}>Reste</th>
                </tr>
              </thead>
              <tbody>
                {bilan.par_type_frais.map(t => (
                  <tr key={t.type_frais_id}>
                    <td style={{ fontWeight: 500 }}>{t.type_frais}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{Number(t.total_du).toLocaleString()}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{Number(t.total_paye).toLocaleString()}</td>
                    <td style={{ whiteSpace: "nowrap", color: Number(t.total_reste) > 0 ? "var(--rd)" : "var(--gn)", fontWeight: 600 }}>
                      {Number(t.total_reste).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {bilan.par_type_frais.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun type de frais engagé</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Trésorerie ───────────────────────────────────── */}
          <SectionTitle icon="ti-cash" titre="Trésorerie" sousTitre={periodeLabel} />
          <div className="sg" style={{ gridTemplateColumns: "repeat(4, minmax(0,1fr))", marginBottom: 14 }}>
            <KpiCard label="Total encaissé (période)" valeur={bilan.tresorerie.total_encaissements} accent="accent-gn" />
            <KpiCard label="Dépenses réelles (période)" valeur={bilan.tresorerie.total_decaissements_reels} accent="accent-rd" />
            <KpiCard
              label="Solde net (période)"
              valeur={bilan.tresorerie.solde_net_periode}
              accent={Number(bilan.tresorerie.solde_net_periode) >= 0 ? "accent-gn" : "accent-rd"}
            />
            <KpiCard label="Trésorerie totale (toutes caisses)" valeur={bilan.tresorerie.tresorerie_totale} accent="accent-bl" />
          </div>
          <div style={{ fontSize: 12, color: "var(--h)", marginBottom: 14, marginTop: -6 }}>
            Dont {Number(bilan.tresorerie.total_versements_eleves).toLocaleString()} FCFA de versements élèves
            et {Number(bilan.tresorerie.total_recettes_libres).toLocaleString()} FCFA de recettes libres (hors suivi élève).
            {Number(bilan.tresorerie.total_annulations) > 0 && (
              <> Décaissé total (dépenses + annulations) : {Number(bilan.tresorerie.total_decaissements).toLocaleString()} FCFA,
              {" "}dont {Number(bilan.tresorerie.total_annulations).toLocaleString()} FCFA d'annulations de versements
              ({bilan.tresorerie.nb_annulations}) — voir le tableau ci-dessous.</>
            )}
          </div>

          {/* ── Caisses ──────────────────────────────────────── */}
          <div className="card" style={{ padding: 0, overflowX: "auto", marginBottom: 8 }}>
            <div className="ch" style={{ padding: "12px 14px 0" }}><div className="ct">Soldes par caisse</div></div>
            <table className="tbl" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th>Caisse</th>
                  <th style={{ whiteSpace: "nowrap" }}>Solde actuel</th>
                  <th style={{ whiteSpace: "nowrap" }}>Encaissé</th>
                  <th style={{ whiteSpace: "nowrap" }}>Décaissé</th>
                  <th style={{ whiteSpace: "nowrap" }}>Transferts</th>
                  <th style={{ whiteSpace: "nowrap" }}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {bilan.caisses.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.nom}</td>
                    <td style={{ fontWeight: 600, color: "var(--pr)", whiteSpace: "nowrap" }}>
                      {Number(c.solde_actuel).toLocaleString()} FCFA
                    </td>
                    <td style={{ whiteSpace: "nowrap", color: "var(--gn)" }}>
                      +{Number(c.encaissements_periode).toLocaleString()}
                    </td>
                    <td style={{ whiteSpace: "nowrap", color: "var(--rd)" }}>
                      -{Number(c.decaissements_periode).toLocaleString()}
                      {Number(c.annulations_periode) > 0 && (
                        <div style={{ fontSize: 11, color: "var(--h)", fontWeight: 400 }}>
                          dont {Number(c.annulations_periode).toLocaleString()} annulé
                        </div>
                      )}
                    </td>
                    <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                      {Number(c.transferts_entree_periode) === 0 && Number(c.transferts_sortie_periode) === 0 ? (
                        <span style={{ color: "var(--h)" }}>—</span>
                      ) : (
                        <>
                          {Number(c.transferts_entree_periode) > 0 && (
                            <div style={{ color: "var(--gn)" }}>+{Number(c.transferts_entree_periode).toLocaleString()} reçu</div>
                          )}
                          {Number(c.transferts_sortie_periode) > 0 && (
                            <div style={{ color: "var(--rd)" }}>-{Number(c.transferts_sortie_periode).toLocaleString()} envoyé</div>
                          )}
                        </>
                      )}
                    </td>
                    <td><span className={`pill ${c.active ? "ok" : "warn"}`}>{c.active ? "Active" : "Inactive"}</span></td>
                  </tr>
                ))}
                {bilan.caisses.length === 0 && (
                  <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune caisse trouvée</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 12, color: "var(--h)", marginBottom: 14 }}>
            La colonne « Transferts » (mouvements d'une caisse vers une autre) est volontairement séparée d'« Encaissé »/« Décaissé » :
            un transfert ne fait qu'entrer et sortir de deux caisses de l'établissement, sans créer de vraie recette ni dépense —
            c'est pourquoi il n'apparaît pas dans les totaux globaux de trésorerie ci-dessus.
          </div>

          {/* ── Répartitions : mode de paiement / rubriques dépenses / annulations
               (chaque tableau seul, empilés verticalement) ── */}
          <div className="bilan-cols">
            <div className="card" style={{ padding: 0, overflowX: "auto" }}>
              <div className="ch" style={{ padding: "12px 14px 0" }}><div className="ct">Versements par mode de paiement</div></div>
              <table className="tbl">
                <thead><tr><th>Mode</th><th style={{ whiteSpace: "nowrap" }}>Montant</th><th style={{ whiteSpace: "nowrap" }}>Nombre</th></tr></thead>
                <tbody>
                  {bilan.par_mode_paiement.map(m => (
                    <tr key={m.mode_paiement}>
                      <td>{m.mode_paiement || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{Number(m.total).toLocaleString()} FCFA</td>
                      <td style={{ whiteSpace: "nowrap" }}>{m.nombre}</td>
                    </tr>
                  ))}
                  {bilan.par_mode_paiement.length === 0 && (
                    <tr><td colSpan="3" style={{ textAlign: "center", color: "var(--h)", padding: "20px" }}>Aucun versement sur la période</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="card" style={{ padding: 0, overflowX: "auto" }}>
              <div className="ch" style={{ padding: "12px 14px 0" }}>
                <div className="ct">Dépenses par rubrique</div>
                <div style={{ fontSize: 11, color: "var(--h)", padding: "2px 14px 8px" }}>
                  Vraies dépenses uniquement — les annulations de versements sont exclues (voir le tableau ci-dessous).
                </div>
              </div>
              <table className="tbl">
                <thead><tr><th>Rubrique</th><th style={{ whiteSpace: "nowrap" }}>Montant</th><th style={{ whiteSpace: "nowrap" }}>Nombre</th></tr></thead>
                <tbody>
                  {bilan.par_rubrique_depense.map(r => (
                    <tr key={r.rubrique}>
                      <td>{r.rubrique || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{Number(r.total).toLocaleString()} FCFA</td>
                      <td style={{ whiteSpace: "nowrap" }}>{r.nombre}</td>
                    </tr>
                  ))}
                  {bilan.par_rubrique_depense.length === 0 && (
                    <tr><td colSpan="3" style={{ textAlign: "center", color: "var(--h)", padding: "20px" }}>Aucune dépense sur la période</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {bilan.annulations_versements.length > 0 && (
              <div className="card" style={{ padding: 0, overflowX: "auto" }}>
                <div className="ch" style={{ padding: "12px 14px 0" }}>
                  <div className="ct">Annulations de versements</div>
                  <div style={{ fontSize: 11, color: "var(--h)", padding: "2px 14px 8px" }}>
                    Contre-passations — de l'argent rendu, pas des dépenses.
                  </div>
                </div>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th style={{ whiteSpace: "nowrap" }}>Reçu</th>
                      <th style={{ whiteSpace: "nowrap" }}>Date</th>
                      <th style={{ whiteSpace: "nowrap" }}>Montant</th>
                      <th>Caisse</th>
                      <th>Motif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilan.annulations_versements.map(a => (
                      <tr key={a.id}>
                        <td style={{ fontWeight: 500, whiteSpace: "nowrap" }}>{a.numero_recu || "—"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{a.date}</td>
                        <td style={{ whiteSpace: "nowrap" }}>{Number(a.montant).toLocaleString()} FCFA</td>
                        <td>{a.caisse}</td>
                        <td style={{ whiteSpace: "normal", fontSize: 12, color: "var(--m)" }}>{a.motif || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2" style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Total annulé</td>
                      <td style={{ fontWeight: 700, color: "var(--rd)", whiteSpace: "nowrap" }}>
                        {Number(bilan.tresorerie.total_annulations).toLocaleString()} FCFA
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        .bilan-cols {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
      `}</style>
    </div>
  );
}

/* ── Petits composants de présentation ─────────────────────────── */

function SectionTitle({ icon, titre, sousTitre }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
      <i className={`ti ${icon}`} style={{ fontSize: 18, color: "var(--pr)" }} />
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--t)" }}>{titre}</div>
      {sousTitre && <div style={{ fontSize: 12, color: "var(--h)" }}>— {sousTitre}</div>}
    </div>
  );
}

function KpiCard({ label, valeur, accent, brut = false }) {
  return (
    <div className={`sc sc-${accent}`}>
      <div className="sclbl">{label}</div>
      <div className="scval" style={{ fontSize: 20 }}>
        {brut ? valeur : `${Number(valeur).toLocaleString()} FCFA`}
      </div>
    </div>
  );
}

function MiniStat({ label, valeur, total, pill }) {
  const pct = total > 0 ? Math.round((valeur / total) * 100) : 0;
  return (
    <div className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{ fontSize: 12, color: "var(--h)" }}>{label}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--t)" }}>{valeur} <span style={{ fontSize: 12, fontWeight: 400, color: "var(--h)" }}>/ {total}</span></div>
      </div>
      <span className={`pill ${pill}`}>{pct}%</span>
    </div>
  );
}

function TauxBar({ du, paye }) {
  const pct = Number(du) > 0 ? Math.min(100, Math.round((Number(paye) / Number(du)) * 100)) : 0;
  const couleur = pct >= 100 ? "var(--gn)" : pct >= 50 ? "#ffc107" : "var(--rd)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 110 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 999, background: "var(--s2)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: couleur }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--t)", whiteSpace: "nowrap" }}>{pct}%</span>
    </div>
  );
}